/**
 * BioPage.jsx
 * The link-in-bio editor.
 *
 * The public address is the first thing on the screen and stays there, because
 * it is the only output of this page — everything else exists to change what
 * that URL shows. It is not clickable until the page is actually published, so
 * a half-finished page is never accidentally shared.
 *
 * Links carry an optional window. That is the feature worth having over a
 * plain list: an offer that ends on Friday stops appearing on Friday with
 * nobody having to remember, and a launch link written on Tuesday appears by
 * itself on Thursday.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import { Link, Warning, Check, Plus, Chart } from '../components/Icons.jsx';
import { formatLondon, toLocalInput } from '../utils/time.js';

export default function BioPage() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      setData(await api.bio.get(brandId));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(fn, message) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      if (message) setNote(message);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  const brand = brands.find((b) => b.id === brandId);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Link in bio</div>
          <h1>One address, all your links</h1>
          <p>
            Instagram allows exactly one link in a profile. This is the menu you point it at, so
            one slot can lead to your site, your app, an offer and anything else — and you can see
            which of them people actually tap.
          </p>
        </div>
        <div className="page-head__actions">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}
      {note && (
        <div className="notice notice--ok">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {!data ? (
        <div className="card">
          <p className="hint">Loading…</p>
        </div>
      ) : !data.page ? (
        <>
          <WhatThisIsFor brand={brand} />
          <Create data={data} brand={brand} act={act} busy={busy} />
        </>
      ) : (
        <>
          <Address data={data} act={act} busy={busy} />
          {data.links.length === 0 && <WhatThisIsFor brand={brand} />}
          <Links data={data} act={act} busy={busy} />
          <Look data={data} act={act} busy={busy} />
          <Clicks data={data} />
        </>
      )}
    </>
  );
}

function Create({ data, brand, act, busy }) {
  const [slug, setSlug] = useState(data.suggestedSlug ?? '');

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Link size={21} />
        </span>
        <div className="card__head-text">
          <h2>Create a page for {brand?.name}</h2>
          <p>It starts unpublished, so nothing is visible until you say so.</p>
        </div>
      </div>

      <div className="field">
        <label htmlFor="bio-slug">Address</label>
        <div className="urlfield">
          <span className="urlfield__prefix">https://{data.host}/b/</span>
          <input
            id="bio-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="archevot"
          />
        </div>
        <p className="hint">
          Lower-case letters, numbers and hyphens. This is what people will type from a phone, so
          shorter is better.
        </p>
      </div>

      <button
        className="btn btn--primary"
        disabled={busy || !slug}
        onClick={() => act(() => api.bio.create({ brandId: brand.id, slug }), 'Page created.')}
      >
        <Plus /> Create the page
      </button>
    </section>
  );
}

/** The address, and whether anyone can see it. */
function Address({ data, act, busy }) {
  const [slug, setSlug] = useState(data.page.slug);
  const live = !!data.page.published;

  // What a visitor would actually see. A page can have links and still be
  // empty to the public if every one of them is hidden or out of its window.
  const visible = data.links.filter((l) => l.live).length;
  const emptyToVisitors = live && visible === 0;

  return (
    <section className="card">
      <div className="card__head">
        <span className={`tile${live ? ' tile--ok' : ''}`}>
          <Link size={21} />
        </span>
        <div className="card__head-text">
          <h2>
            {live ? 'Live' : 'Not published'}
            <span className={`pill pill--${live ? 'ok' : 'muted'}`}>
              {live ? `${data.page.views} views` : 'nobody can see it yet'}
            </span>
          </h2>
          <p>
            {live ? (
              <a href={data.url} target="_blank" rel="noreferrer">
                {data.url}
              </a>
            ) : (
              // Deliberately not a link while unpublished: it would 404, which
              // looks like a broken app rather than a deliberate state.
              <span className="mono-wrap">{data.url}</span>
            )}
          </p>
        </div>
      </div>

      {/* The failure this guards: a page published with nothing on it. Anyone
          who opens the link gets a dead end with the brand's name on it, and
          the person who published it never finds out. */}
      {emptyToVisitors && (
        <div className="notice notice--error">
          <Warning />
          <span>
            <b>This page is live but has nothing on it.</b> Anyone opening the link sees an empty
            page with {data.page.title || 'your brand'}’s name on it. Add a link below, or take the
            page down until you have one.
          </span>
        </div>
      )}

      <div className="field">
        <label htmlFor="bio-slug2">Address</label>
        <div className="urlfield">
          <span className="urlfield__prefix">https://{data.host}/b/</span>
          <input id="bio-slug2" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} />
        </div>
        {slug !== data.page.slug && (
          <p className="hint">
            Changing this breaks the old address for anyone who has it saved.
          </p>
        )}
      </div>

      <div className="btn-row">
        {slug !== data.page.slug && (
          <button
            className="btn"
            disabled={busy}
            onClick={() => act(() => api.bio.update(data.page.id, { slug }), 'Address changed.')}
          >
            Change the address
          </button>
        )}

        <button
          className={`btn ${live ? '' : 'btn--primary'}`}
          disabled={busy || (!live && visible === 0)}
          title={
            !live && visible === 0
              ? 'Add a link first — publishing an empty page gives visitors a dead end'
              : ''
          }
          onClick={() =>
            act(
              () => api.bio.update(data.page.id, { published: !live }),
              live ? 'Taken down. The address now shows nothing.' : 'Published.',
            )
          }
        >
          {live ? 'Take it down' : 'Publish it'}
        </button>

        {live && (
          <button className="btn" onClick={() => navigator.clipboard?.writeText(data.url)}>
            Copy the link
          </button>
        )}
      </div>
    </section>
  );
}

function Links({ data, act, busy }) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(null);

  const move = (index, by) => {
    const ids = data.links.map((l) => l.id);
    const to = index + by;
    if (to < 0 || to >= ids.length) return;
    [ids[index], ids[to]] = [ids[to], ids[index]];
    act(() => api.bio.reorder(data.page.id, ids));
  };

  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>Links</h2>
          <p>
            {data.links.length} of {data.limits.maxLinks}. In the order they appear on the page.
          </p>
        </div>
      </div>

      {data.links.length === 0 ? (
        <p className="hint">No links yet.</p>
      ) : (
        <div className="rows">
          {data.links.map((link, i) => (
            <div className="row" key={link.id}>
              <div className="reorder">
                <button
                  className="reorder__btn"
                  disabled={busy || i === 0}
                  onClick={() => move(i, -1)}
                  aria-label="Move up"
                >
                  ↑
                </button>
                <button
                  className="reorder__btn"
                  disabled={busy || i === data.links.length - 1}
                  onClick={() => move(i, 1)}
                  aria-label="Move down"
                >
                  ↓
                </button>
              </div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="row__label">{link.label}</div>
                <div className="row__sub mono-wrap">{link.url}</div>
                {(link.starts_at || link.ends_at) && (
                  <div className="row__sub">
                    {link.starts_at && `from ${formatLondon(link.starts_at)}`}
                    {link.starts_at && link.ends_at && ' · '}
                    {link.ends_at && `until ${formatLondon(link.ends_at)}`}
                  </div>
                )}
              </div>

              <div className="row__end">
                <span className={`badge${link.live ? ' badge--ok' : ''}`}>{link.why}</span>
                <span className="badge">{link.clicks} clicks</span>
                <button className="btn btn--sm" onClick={() => setOpen(open === link.id ? null : link.id)}>
                  Edit
                </button>
                <button
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => act(() => api.bio.updateLink(link.id, { enabled: !link.enabled }))}
                >
                  {link.enabled ? 'Hide' : 'Show'}
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  disabled={busy}
                  onClick={() => act(() => api.bio.removeLink(link.id))}
                >
                  Delete
                </button>
              </div>

              {open === link.id && <EditLink link={link} act={act} busy={busy} onDone={() => setOpen(null)} />}
            </div>
          ))}
        </div>
      )}

      <div className="inline-form">
        <div className="field">
          <label htmlFor="bl-label">Label</label>
          <input id="bl-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Download the app" />
        </div>
        <div className="field" style={{ flex: '2 1 240px' }}>
          <label htmlFor="bl-url">Address</label>
          <input id="bl-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
        </div>
        <div className="field">
          <label htmlFor="bl-note">Note (optional)</label>
          <input id="bl-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="iOS and Android" />
        </div>
        <button
          className="btn btn--primary"
          disabled={busy || !label.trim() || !url.trim()}
          onClick={() =>
            act(async () => {
              await api.bio.addLink(data.page.id, { label, url, note });
              setLabel('');
              setUrl('');
              setNote('');
            })
          }
        >
          <Plus /> Add
        </button>
      </div>
    </section>
  );
}

/** The window, which is the part worth having over a plain list. */
function EditLink({ link, act, busy, onDone }) {
  const [startsAt, setStartsAt] = useState(toLocalInput(link.starts_at) || '');
  const [endsAt, setEndsAt] = useState(toLocalInput(link.ends_at) || '');

  return (
    <div className="row__expand">
      <div className="inline-form" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
        <div className="field">
          <label htmlFor={`s-${link.id}`}>Show from</label>
          <input
            id={`s-${link.id}`}
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`e-${link.id}`}>Stop showing</label>
          <input
            id={`e-${link.id}`}
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </div>
        <button
          className="btn btn--primary"
          disabled={busy}
          onClick={() =>
            act(async () => {
              await api.bio.updateLink(link.id, {
                starts_at: startsAt ? new Date(startsAt).toISOString() : null,
                ends_at: endsAt ? new Date(endsAt).toISOString() : null,
              });
              onDone();
            })
          }
        >
          Save
        </button>
        <button className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
      <p className="hint">
        Leave both blank to show it always. London time.
      </p>
    </div>
  );
}

function Look({ data, act, busy }) {
  const [title, setTitle] = useState(data.page.title);
  const [bio, setBio] = useState(data.page.bio);
  const [accent, setAccent] = useState(data.page.accent);
  const [theme, setTheme] = useState(data.page.theme);

  const changed =
    title !== data.page.title ||
    bio !== data.page.bio ||
    accent !== data.page.accent ||
    theme !== data.page.theme;

  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>How it looks</h2>
        </div>
      </div>

      <div className="field">
        <label htmlFor="bio-title">Heading</label>
        <input id="bio-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={data.limits.maxTitle} />
      </div>

      <div className="field">
        <label htmlFor="bio-text">A line about you</label>
        <textarea
          id="bio-text"
          rows={2}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={data.limits.maxBio}
        />
      </div>

      <div className="inline-form" style={{ borderTop: 'none', paddingTop: 0 }}>
        <div className="field" style={{ flex: '0 0 120px' }}>
          <label htmlFor="bio-accent">Colour</label>
          <input id="bio-accent" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} />
        </div>
        <div className="field" style={{ flex: '0 0 140px' }}>
          <label htmlFor="bio-theme">Theme</label>
          <select id="bio-theme" value={theme} onChange={(e) => setTheme(e.target.value)}>
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </div>
        <button
          className="btn btn--primary"
          disabled={busy || !changed}
          onClick={() => act(() => api.bio.update(data.page.id, { title, bio, accent, theme }), 'Saved.')}
        >
          Save
        </button>
      </div>
    </section>
  );
}

function Clicks({ data }) {
  if (data.totalClicks === 0) return null;

  const top = Math.max(...data.clicks.map((d) => d.clicks), 1);

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Chart size={21} />
        </span>
        <div className="card__head-text">
          <h2>{data.totalClicks} clicks</h2>
          <p>
            Counted on the redirect, so it works with JavaScript off. No addresses or devices are
            recorded — only which link, and on which day.
          </p>
        </div>
      </div>

      <div className="daybars">
        {data.clicks.map((d) => (
          <div className="daybars__row" key={d.day}>
            <span className="daybars__day">{d.day.slice(5)}</span>
            <span className="daybars__track">
              <span className="daybars__fill" style={{ width: `${(d.clicks / top) * 100}%` }} />
            </span>
            <span className="daybars__count">{d.clicks}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * What the page is actually for.
 *
 * Shown only while there is nothing on the page, because that is exactly when
 * it is a mystery: a blank page at an address you were given, with no
 * indication of what was supposed to be there. It is not a website and it is
 * not a redirect, and being told so plainly is worth more than being left to
 * work it out.
 */
function WhatThisIsFor({ brand }) {
  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Link size={21} />
        </span>
        <div className="card__head-text">
          <h2>What this page is for</h2>
          <p>It is a menu, not a website. Worth being clear about before you build one.</p>
        </div>
      </div>

      <ul className="plainlist">
        <li>
          <b>The problem it solves.</b> Instagram gives a profile one link. If you want to send
          people to the app store, a sign-up page and a blog post, you have one slot for three
          destinations.
        </li>
        <li>
          <b>What visitors see.</b> This page, with a button for each destination. They tap the one
          they want. It is deliberately plain and loads instantly on a phone.
        </li>
        <li>
          <b>What you get back.</b> A count of which links people actually tap, which the bio slot
          alone can never tell you — and the ability to change where the link goes without editing
          your profile.
        </li>
        <li>
          <b>When not to bother.</b> If you only ever want to send people to one place, skip this
          and put {brand?.target_url ? <code>{brand.target_url}</code> : 'that address'} straight in
          your bio. An extra tap for no reason is worse than no page at all.
        </li>
      </ul>
    </section>
  );
}
